import { forwardRef, useEffect, useImperativeHandle, useState, useCallback, useMemo } from "react"
import { Form, SliderSingleProps, Input, Select as AntSelect } from "antd"
import { AddModelObjectProvider } from "components/providers/Forms.provider"
import { Slider } from "components/ui/slider/Slider"
import { H4 } from "components/ui/typography/titles/Titles"
import { Select } from "components/ui/select/Select"
import { useCategories } from "hooks/categories.hook"
import { createColumnBasedCategoryStore } from "store/columnBasedCategory.store"
import { useWithoutCategoryModelNumbers } from "hooks/withoutCategoryModelNumbers.hook"
import { OBJECT_LABEL_VALIDATION, OBJECT_VALIDATION_MESSAGES } from "helpers/validation/objectValidation.helper"
import type { IAddObjectMethods } from "types/configuration.type"
import type { TTopLevelCategory } from "types/drawer.type"
import style from "./ColumnBasedObject.module.scss"

const {
	columnBasedObjectForm,
	block,
	label,
	columnsLabel,
	labelWrapper,
	columnHelperText,
	endOfSection,
	description,
	title,
	select,
	startOfSection,
} = style

type Props = { objectCategoriesData: TTopLevelCategory[] }

const sliderMarks: SliderSingleProps["marks"] = {
	1: "1",
	2: "2",
	3: "3",
	4: "4",
	5: "5",
	6: "6",
}

export const ColumnBasedObject = forwardRef<IAddObjectMethods, Props>(({ objectCategoriesData }, ref) => {
	const [selectedTopLevelCategory, setSelectedTopLevelCategory] = useState<string | number | undefined>()
	const {
		category,
		setFormValues,
		updateColumnsRange,
		setColumnModelNumbers,
		resetForm: resetStoreForm,
	} = createColumnBasedCategoryStore()
	const { topLevelCategories, superCategories } = useCategories(objectCategoriesData, selectedTopLevelCategory)
	const { options: modelNumberOptions } = useWithoutCategoryModelNumbers()

	const [form] = Form.useForm()
	const values = Form.useWatch([], form)

	// Calculate number of objects based on columns range
	const columnsRange = values?.columns || [1, 2]
	const numberOfObjects = columnsRange[1] - columnsRange[0] + 1

	const onChangeTopLevelCategoryHandle = (value: string | number) => {
		setSelectedTopLevelCategory(value)
		form.resetFields(["superCategory"])
	}

	useImperativeHandle(ref, () => ({
		resetForm: () => {
			form.resetFields()
			resetStoreForm()
		},
	}))

	useEffect(() => {
		setFormValues({
			name: values?.name,
			label: values?.label,
			columns: values?.columns,
			topLevelCategory: values?.topLevelCategory,
			superCategory: values?.superCategory,
		})
	}, [setFormValues, values])

	// Handle columns range change
	useEffect(() => {
		if (values?.columns && JSON.stringify(values.columns) !== JSON.stringify(category.columns)) {
			updateColumnsRange(values.columns)
		}
	}, [values?.columns, category.columns, updateColumnsRange])

	const makeOnColumnModelNumbersChange = useCallback(
		(objectPosition: string) => (selectedModelNumbers: number[]) => {
			setColumnModelNumbers(objectPosition, selectedModelNumbers)
		},
		[setColumnModelNumbers],
	)

	const getOptionsForPosition = useMemo(
		() => (objectPosition: string) => {
			const otherSelected = Object.entries(category.modelNumbers)
				.filter(([pos]) => pos !== objectPosition)
				.flatMap(([, nums]) => nums)
			return modelNumberOptions.filter((opt) => !otherSelected.includes(opt.value))
		},
		[modelNumberOptions, category.modelNumbers],
	)

	return (
		<AddModelObjectProvider>
			<Form form={form} size="large">
				<div className={columnBasedObjectForm}>
					<div className={`${block} ${endOfSection}`}>
						<div className={labelWrapper}>
							<span className={columnsLabel}>Number of columns</span>
							<span className={columnHelperText}>{numberOfObjects} objects will be created</span>
						</div>
						<Form.Item
							name="columns"
							initialValue={[1, 2]}
							rules={[{ required: true, message: OBJECT_VALIDATION_MESSAGES.COLUMNS_REQUIRED }]}
						>
							<Slider marks={sliderMarks} step={1} min={1} max={6} />
						</Form.Item>
					</div>
					<div className={block}>
						<span className={label}>Name</span>
						<Form.Item name="name" rules={[{ required: true, message: OBJECT_VALIDATION_MESSAGES.NAME_REQUIRED }]}>
							<Input data-testid="name" />
						</Form.Item>
					</div>
					<div className={`${block} ${endOfSection}`}>
						<span className={label}>Label</span>
						<span className={description}>
							<span className={description}>Used in train set, without [r], [l], or [w]</span>
							<Form.Item
								name="label"
								required={true}
								rules={[
									{ required: true, message: OBJECT_VALIDATION_MESSAGES.LABEL_REQUIRED },
									OBJECT_LABEL_VALIDATION.LABEL_PREFIX,
								]}
							>
								<Input data-testid="label" />
							</Form.Item>
						</span>
					</div>
					<div className={block}>
						<H4 className={title}>Categories</H4>
						<span className={label}>Top level category</span>
						<Form.Item
							name="topLevelCategory"
							required={true}
							rules={[{ required: true, message: OBJECT_VALIDATION_MESSAGES.TOP_LEVEL_CATEGORY_REQUIRED }]}
						>
							<Select
								placeholder="Select category"
								allowClear={false}
								variant="outlined"
								options={topLevelCategories}
								onChange={onChangeTopLevelCategoryHandle}
								className={select}
							/>
						</Form.Item>
					</div>
					<div className={block}>
						<span className={label}>Super category</span>
						<Form.Item
							name="superCategory"
							required={true}
							rules={[{ required: true, message: OBJECT_VALIDATION_MESSAGES.SUPER_CATEGORY_REQUIRED }]}
						>
							<Select
								placeholder="Select category"
								variant="outlined"
								allowClear={false}
								className={select}
								options={superCategories}
							/>
						</Form.Item>
					</div>
					<div className={`${block} ${startOfSection}`}>
						<H4 className={title}>Model numbers</H4>
						{Array.from({ length: numberOfObjects }, (_, index) => {
							const columnNumber = columnsRange[0] + index
							const objectPosition = columnNumber.toString()
							return (
								<div key={index} style={{ marginBottom: "32px" }}>
									<span className={label}>Object {columnNumber}</span>
									<AntSelect
										mode="multiple"
										showSearch
										placeholder="Select model numbers"
										style={{ width: "100%" }}
										value={category.modelNumbers[objectPosition] || []}
										onChange={makeOnColumnModelNumbersChange(objectPosition)}
										options={getOptionsForPosition(objectPosition)}
										optionFilterProp="label"
										data-testid={`column-model-numbers-select-${objectPosition}`}
									/>
								</div>
							)
						})}
					</div>
				</div>
			</Form>
		</AddModelObjectProvider>
	)
})
